// Split v1.14
// Breaks text holder elements into individual letter/word/line span parts.

// usage:
// uses these default selectors:
// characters = '.split-chars',
// words = '.split-words',
// lines = '.split-lines',
// splitter = new Split();
// add this sass to custom selectors: &:not(:has(.split)) { visibility: hidden; } 
// to prevent showing un-split content before js is loaded. For the default selectors, this is already in the css.

// possible properties:
// charTiming = { delay: 0, stagger: 50 }: delay config object for character splits
// wordTiming = { delay: 0, stagger: 100 }: delay config object for word splits 
// lineTiming = { delay: 0, stagger: 200 }: delay config object for line splits
// charHolderSelector = '.chars': selector for holders that should split into characters
// wordHolderSelector = '.words': selector for holders that should split into words
// lineHolderSelector = '.lines': selector for holders that should split into visual lines
// replacedChar = '±': placeholder used while splitting HTML tags safely
// textClass = 'fader fader--up': classes applied to text split parts
// imgClass = 'fader fader--loader': classes applied to image split parts
// isSafari = false: skip splitting holders with SVG filters on Safari and fade the whole holder instead

// methods:
// splitter.setSplitters(): need to be called after new content is added to the page
// splitter.destroy(): remove resize listener

// helper notes:
// add no-split-delay class to holder element to skip stagger delay
// is not using default selectors, add &:not(:has(.split)) { visibility: hidden; } to the holder element in css to prevent showing un-split content before js is loaded. For the default selectors, this is already in the css.

// changelog:
// 1.0: initial version
// 1.1: some cleanup 
// 1.2: fix for splitting an element twice
// 1.3: output data-fader-delay attribute even when delay is 0
// 1.4: remove fader--force, not needed from fader 9.0
// 1.5: make internal methods private, keep setSplitters as public API
// 1.6: tiny cleanup, remove getElementTop helper that is not used anymore
// 1.7: add destroy(), fix noDelayClass not assigned to this
// 1.8: wrap split chars per word so words don't get separated when they wrap
// 1.9: add Safari fallback for holders with svg filters, fade the whole holder instead of splitting
// 1.10: on split chars, a break now also creates a new word wrapper
// 1.11: add split-space--no-width when a space starts a wrapped line
// 1.12: use split-space elements between split words instead of css word spacing
// 1.13: trim leading and trailing whitespace so surrounding spaces are ignored during splitting
// 1.14: collapse repeated whitespace runs to a single space before splitting

import {getPageCoordinates} from './GetCoordinates.js';

export default class Split {
  #boundResize;

  constructor({
    charHolderSelector = '.split-chars',
    wordHolderSelector = '.split-words',
    lineHolderSelector = '.split-lines',
    replacedChar = '±',
    charTiming = { delay: 0, stagger: 50 },
    wordTiming = { delay: 0, stagger: 100 },
    lineTiming = { delay: 0, stagger: 200 },
    textClass = 'fader fader--up',
    imgClass = 'fader fader--loader',
    isSafari = false,
  } = {}) {
    // config variables
    this.charHolderSelector = charHolderSelector;
    this.wordHolderSelector = wordHolderSelector;
    this.lineHolderSelector = lineHolderSelector;
    this.replacedChar = replacedChar;
    this.charTiming = charTiming;
    this.wordTiming = wordTiming;
    this.lineTiming = lineTiming;
    this.textClass = textClass;
    this.imgClass = imgClass;
    this.isSafari = isSafari;

    // code variables
    this.noDelayClass = 'no-split-delay';

    this.#boundResize = () => requestAnimationFrame(() => this.#resize());
    window.visualViewport.addEventListener('resize', this.#boundResize);
  }

  destroy = () => {
    window.visualViewport.removeEventListener('resize', this.#boundResize);
  }

  setSplitters = () => {
    const charHolderEls = document.body.querySelectorAll(this.charHolderSelector);
    const wordHolderEls = document.body.querySelectorAll(this.wordHolderSelector);
    const lineHolderEls = document.body.querySelectorAll(this.lineHolderSelector);
    this.#splitChars(charHolderEls);
    this.#splitWords(wordHolderEls, this.wordTiming);
    this.#splitWords(lineHolderEls, this.lineTiming || this.wordTiming);
  }

  #resize() {
    const lineHolderEls = document.body.querySelectorAll(this.lineHolderSelector);
    const charHolderEls = document.body.querySelectorAll(this.charHolderSelector);
    const wordHolderEls = document.body.querySelectorAll(this.wordHolderSelector);
    const spaceHolderEls = new Set([...charHolderEls, ...wordHolderEls, ...lineHolderEls]);

    lineHolderEls.forEach(holderEl => {
      let line = 0;
      let prevTop = 0;
      const partsEls = holderEl.querySelectorAll('.split__text, .split__img');

      partsEls.forEach(partEl => {
        const top = getPageCoordinates(partEl).top;
        if (top > prevTop) line++;

        prevTop = top;
        partEl.dataset.faderDelay = this.#getDelay(holderEl, this.lineTiming, line);
      });
    });

    spaceHolderEls.forEach(holderEl => {
      let prevTop;

      const splitEls = holderEl.querySelectorAll('.split-word, .split-space');

      splitEls.forEach(splitEl => splitEl.classList.remove('split-space--no-width'));
      
      splitEls.forEach(splitEl => {
        const top = getPageCoordinates(splitEl).top;

        if (splitEl.classList.contains('split-space') && prevTop !== undefined && top > prevTop) splitEl.classList.add('split-space--no-width');

        prevTop = top;
      });
    });
  }

  #splitChars(holderEls) {
    holderEls.forEach(holderEl => {
      if (this.#shouldFadeHolder(holderEl)) {
        holderEl.classList.add('fader');
        holderEl.style.visibility = 'visible';
        return;
      }

      if (holderEl.querySelector('.split')) return;

      let staggerTotal = 0;
      let newHtml = '<div class="split-word">';
      const tokens = holderEl.innerHTML.split(/(<br\s*\/?>)/i);

      tokens.forEach(token => {
        const normalizedToken = this.#normalizeToken(token);

        if (!normalizedToken) return;

        if (normalizedToken.match(/<br\s*\/?>/i)) {
          newHtml += `</div><span class="split-break"></span><div class="split-word">`;
          return;
        }

        const tempEl = document.createElement('div');
        tempEl.innerHTML = normalizedToken;
        const letters = tempEl.textContent.split('');

        letters.forEach(letter => {
          if (letter === ' ') {
            newHtml += `</div><span class="split-space">&nbsp;</span><div class="split-word">`;
          } else {
            const delay = this.#getDelay(holderEl, this.charTiming, staggerTotal);
            // console.log({ letter, delay });

            newHtml += `<span class="split">`;
            newHtml += `<div class="split__text ${this.textClass}"${delay !== '' ? ` data-fader-delay="${delay}"` : ''}>${letter}</div>`;
            newHtml += `</span> `;
            staggerTotal++;
          }
        });
      });

      newHtml += '</div>';

      holderEl.innerHTML = newHtml;
    });
  }

  #splitWords(holderEls, delayConfig) {
    holderEls.forEach(holderEl => {
      if (this.#shouldFadeHolder(holderEl)) {
        holderEl.classList.add('fader');
        holderEl.style.visibility = 'visible';
        return;
      }

      if (holderEl.querySelector('.split')) return;

      let staggerTotal = 0;
      const tokens = holderEl.innerHTML.split(/(<br\s*\/?>)/i);
      let newHtml = '';

      tokens.forEach(token => {
        const normalizedToken = this.#normalizeToken(token);

        if (!normalizedToken) return;

        if (normalizedToken.match(/<br\s*\/?>/i)) {
          newHtml += `<span class="split-break"></span>`;
        } else {
          const parts = normalizedToken.split(' ').filter(part => part.length > 0);

          parts.forEach((part, index) => {
            if (index > 0) {
              newHtml += '<span class="split-space">&nbsp;</span>';
            }

            const delay = this.#getDelay(holderEl, delayConfig, staggerTotal);
            // console.log({ word: part, delay });

            newHtml += `<div class="split-word"><span class="split split--word">`;
            if (part === this.replacedChar) {
              const src = holderEl.dataset.src || '';
              const width = holderEl.dataset.width || '1';
              const alignment = holderEl.dataset.alignment || 'center';
              newHtml += `  <img class="split__img split__img--${alignment} ${this.imgClass}" data-src="${src}" decoding="async" style="--width: ${width};"${delay !== '' ? ` data-fader-delay="${delay}"` : ''}>`;
            } else {
              newHtml += `  <div class="split__text ${this.textClass}"${delay !== '' ? ` data-fader-delay="${delay}"` : ''}>${part}</div>`;
            }
            newHtml += `</span></div>`;
            staggerTotal++;
          });
        }
      });

      holderEl.innerHTML = newHtml;
    });

    // call resize to set line numbers for the word splitters, so they can use that for delay if wanted
    this.#resize();
  }

  #normalizeToken(token) {
    return token.trim().replace(/\s+/g, ' ');
  }

  #getDelay(holderEl, delayConfig, index) {
    if (holderEl.classList.contains(this.noDelayClass) || !delayConfig) return '';
    return (delayConfig.delay || 0) + index * (delayConfig.stagger || 0);
  }

  #shouldFadeHolder(holderEl) {
    return this.isSafari && this.#hasSvgFilter(holderEl);
  }

  #hasSvgFilter(holderEl) {
    const inlineStyle = holderEl.getAttribute('style') || '';

    if (/filter:\s*url\(/i.test(inlineStyle) || /-webkit-filter:\s*url\(/i.test(inlineStyle)) {
      return true;
    }

    const computedStyle = window.getComputedStyle(holderEl);
    const filter = computedStyle.getPropertyValue('filter');
    const webkitFilter = computedStyle.getPropertyValue('-webkit-filter');

    return /url\(/i.test(filter) || /url\(/i.test(webkitFilter);
  }
};